import { useState, useCallback } from 'react';
import { DraftHandleValue, getDefaultKeyBinding } from 'draft-js';


/**
 * Props for useSuggestionList hook
 */
interface UseSuggestionListProps {
  filteredSuggestions: readonly string[];
  onSuggestionSelect: (suggestion: string) => void;
}


/**
 * Result object returned by useSuggestionList hook
 */
interface UseSuggestionListResult {
  dropdownVisible: boolean;
  setDropdownVisible: (visible: boolean) => void;
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  handleKeyCommand: (command: string) => DraftHandleValue;
  keyBindingFn: (e: React.KeyboardEvent) => string | null;
  selectSuggestion: (suggestion: string) => void;
}


/**
 * Hook to manage suggestion list keyboard navigation and selection.
 * Handles arrow key navigation, selection with enter/tab, and mouse selection.
 *
 * @param props - Object containing filtered suggestions and selection callback
 * @returns Object with state and handlers for suggestion list functionality
 */
export const useSuggestionList = ({
  filteredSuggestions,
  onSuggestionSelect
}: UseSuggestionListProps): UseSuggestionListResult => {
  const [dropdownVisible, setDropdownVisible] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
 
  // Handle suggestion selection via click or keyboard
  const selectSuggestion = useCallback((suggestion: string): void => {
    onSuggestionSelect(suggestion);
    setDropdownVisible(false);
    setSelectedIndex(0);
  }, [onSuggestionSelect]);


  // Handle keyboard commands for navigation and selection
  const handleKeyCommand = useCallback((command: string): DraftHandleValue => {
    if (dropdownVisible) {
      switch (command) {
        case 'arrow-up':
          setSelectedIndex((prevIndex) =>
            prevIndex > 0 ? prevIndex - 1 : prevIndex
          );
          return 'handled';
        case 'arrow-down':
          setSelectedIndex((prevIndex) =>
            prevIndex < filteredSuggestions.length - 1 ? prevIndex + 1 : prevIndex
          );
          return 'handled';
        case 'suggestion-enter':
          if (selectedIndex >= 0) {
            selectSuggestion(filteredSuggestions[selectedIndex]);
          }
          return 'handled';
      }
    }
    return 'not-handled';
  }, [dropdownVisible, filteredSuggestions, selectedIndex, selectSuggestion]);


  // Map keyboard events to commands
  const keyBindingFn = useCallback((e: React.KeyboardEvent): string | null => {
    if (dropdownVisible) {
      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          return 'arrow-up';
        case 'ArrowDown':
          e.preventDefault();
          return 'arrow-down';
        case 'Enter':
        case 'Tab':
          e.preventDefault();
          return 'suggestion-enter';
      }
    }
    return getDefaultKeyBinding(e);
  }, [dropdownVisible]);


  return {
    dropdownVisible,
    setDropdownVisible,
    selectedIndex,
    setSelectedIndex,
    handleKeyCommand,
    keyBindingFn,
    selectSuggestion
  };
};